import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { BarChart3, Printer } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import {
  EXCHANGE_DIMENSIONS,
  REPORT_CUTS,
  REPORT_DEFINITIONS,
  REPORT_IDS,
  type ExchangeDimension,
  type ReportColumn,
  type ReportId,
} from '@aeris/contracts'
import { formatMoney, parseHumanAmount } from '@aeris/money'
import { reportPrintFor } from '@aeris/printing'
import {
  Button,
  EmptyState,
  InlineAlert,
  ListToolbar,
  Page,
  PageHeader,
  QueryState,
  Select,
  StatTile,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { ExportMenu } from '../components/export-menu.tsx'
import { PartyPicker } from '../components/party-picker.tsx'
import { PeriodPicker } from '../components/period-picker.tsx'
import { PrintDocumentDialog } from '../components/print-document-dialog.tsx'
import { ReturnedPiecesPanel } from '../components/returned-pieces-panel.tsx'
import { SalesPeriodReport } from '../components/sales-period-report.tsx'
import { fetchReport } from '../lib/api/reports.ts'
import { fetchBranches } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { useBalcaoAnterior } from '../lib/balcao-anterior.ts'
import { formatDate } from '../lib/date-format.ts'
import { customRange, exclusiveEnd, periodRange } from '../lib/period-range.ts'
import {
  reportExportColumns,
  reportExportRows,
  type ReportExportRow,
} from '../lib/report-export.ts'
import {
  SALES_REPORT,
  type ReportChoice,
  type ReportSearch,
} from '../lib/report-search.ts'
import type { SalesReportMode } from '../lib/sales-period-report.ts'
import {
  useCompanyHeader,
  useReceiptTemplate,
} from '../lib/use-receipt-template.ts'

/**
 * Relatórios — F158.
 *
 * > "quero que tudo do admin consiga virar relatório, exportar métrica, ver top
 * > 1 cliente top 10"
 *
 * ## Uma tela para quatro perguntas
 *
 * As colunas de cada relatório são DADO (`REPORT_DEFINITIONS`), não JSX. É o
 * que permite que esta tela, o arquivo exportado e o servidor concordem sobre o
 * que existe — e que um relatório novo seja uma entrada num mapa em vez de uma
 * quinta tela parecida.
 *
 * ## O que nunca some
 *
 * **"Outros" e "Total"** ficam sempre no fim da tabela, rotulados. Um top 10
 * sem eles é lido como o todo — "top 10 = R$ 40 mil" vira "vendi 40 mil", e
 * alguém decide compra em cima disso. A conta fecha por construção: o servidor
 * soma os dois no mesmo SQL das linhas.
 *
 * **A cobertura**, quando o dado não está cheio. O relatório de motivo começa
 * vazio — nenhuma troca anterior à migração 0086 tem código —, e um ranking
 * sobre 9% do dado, sem a frase, parece o retrato da loja.
 *
 * ## O estado vive na URL
 *
 * Esta é a **primeira tela do produto** a guardar filtros na URL, e vale a
 * exceção: um relatório se manda por WhatsApp como link, sobrevive ao
 * recarregar, e a janela do Modo PC (F138) o restaura. A disciplina é a de
 * `router.tsx`: **valor inválido vira ausência**, e a tela abre no padrão.
 */

const CUT_LABEL = (cut: number): string => `Top ${String(cut)}`

/**
 * Quais colunas viram cartão no topo, e com que nome (F164).
 *
 * Só as quatro que respondem "como foi o período": o resto é do ranking. A
 * margem não é cartão — é o `hint` do Lucro, como no painel.
 */
const TILE_COLUMNS = ['revenue', 'cost', 'profit', 'returned', 'amount']
const TILE_LABEL: Readonly<Record<string, string>> = {
  amount: 'Devolvido no período',
}

const DIMENSION_LABEL: Record<ExchangeDimension, string> = {
  marca: 'Por marca',
  categoria: 'Por categoria',
  produto: 'Por produto',
  motivo: 'Por motivo',
  vendedor: 'Por vendedor',
}

/**
 * Como cada célula é escrita.
 *
 * Dinheiro com `formatMoney` (nunca `Number`), quantidade sem os zeros à
 * direita, percentual com o sinal, data curta. Vazio vira travessão — e não
 * "0", que seria uma afirmação diferente.
 */
/** Dinheiro do fio, escrito em reais. Nunca passa por `Number` (ADR-0016). */
const money = (raw: string): string => {
  const amount = parseHumanAmount(raw)
  return amount === null ? raw : formatMoney(amount)
}

const cell = (column: ReportColumn, raw: string | null | undefined): string => {
  if (raw === null || raw === undefined || raw === '') return '—'
  switch (column.kind) {
    case 'money':
      return money(raw)
    case 'quantity':
      return raw.replace(/\.?0+$/, '').replace('.', ',')
    case 'percent':
      return `${raw.replace('.', ',')}%`
    case 'date':
      return formatDate(raw)
    case 'count':
    case 'text':
      return raw
  }
}

export const RelatoriosPage = (): ReactNode => {
  const { allows } = useAuth()
  const classic = useBalcaoAnterior()
  const navigate = useNavigate()
  const search: ReportSearch = useSearch({ from: '/shell/relatorios' })

  const choiceOfReport: ReportChoice = search.relatorio ?? 'top-clientes'
  const salesList = choiceOfReport === SALES_REPORT
  /* A lista de vendas (F271) não usa o envelope dos rankings. */
  const report: ReportId =
    choiceOfReport === SALES_REPORT ? 'top-clientes' : choiceOfReport
  const customerId = search.cliente ?? ''
  const salesMode: SalesReportMode = search.modo ?? 'detalhado'
  const choice = search.periodo ?? 'month'
  const dimension = search.por ?? 'marca'
  const cut = search.corte ?? 10
  const branchId = search.filial ?? ''
  const customFrom = search.de ?? ''
  const customTo = search.ate ?? ''

  const set = (next: Partial<ReportSearch>): void => {
    void navigate({
      to: '/relatorios',
      search: (current: ReportSearch) => ({ ...current, ...next }),
      replace: true,
    })
  }

  const range =
    choice === 'custom'
      ? customRange(customFrom, customTo)
      : periodRange(choice)

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: async ({ signal }) => await fetchBranches(undefined, signal),
    enabled: allows('organization.read'),
  })
  const branchList = branches.data?.branches ?? []

  /*
   * O filtro que está na tela, sem nada a mais.
   *
   * Um objeto só, lido pela consulta E pela exportação — é o que impede o
   * arquivo de discordar do que a pessoa está vendo. Mesmo padrão de
   * `vendas.tsx`.
   */
  const filter = useMemo(
    () =>
      range === null
        ? null
        : {
            report,
            from: range.from,
            to: exclusiveEnd(range.to),
            cut,
            ...(branchId === '' ? {} : { branchId }),
            ...(report === 'trocas' ? { dimension } : {}),
          },
    [report, range, cut, branchId, dimension]
  )

  const query = useQuery({
    queryKey: ['report', filter],
    queryFn: async ({ signal }) => {
      if (filter === null) throw new Error('período incompleto')
      return await fetchReport(filter, signal)
    },
    enabled: filter !== null && !salesList,
  })

  const page = query.data
  const definition = REPORT_DEFINITIONS[report]

  const exportRows: readonly ReportExportRow[] =
    page === undefined ? [] : reportExportRows(page)

  /*
   * O relatório no papel — F160.
   *
   * O gerente: "só na hora de imprimir o relatório". Montado das MESMAS linhas
   * da tela e do Excel, com "Outros" e "Total" no fim: um top 10 impresso sem
   * eles é lido como se fosse o todo.
   */
  const [printing, setPrinting] = useState(false)
  const company = useCompanyHeader()
  const template = useReceiptTemplate()
  const printable =
    page === undefined || range === null
      ? null
      : reportPrintFor({
          company,
          template,
          title: definition.label,
          filters: [
            {
              label: 'Período',
              value: `${formatDate(range.from)} a ${formatDate(range.to)}`,
            },
            {
              label: 'Filial',
              value:
                branchList.find((branch) => branch.id === branchId)?.name ??
                'Todas',
            },
            { label: 'Corte', value: `Top ${String(cut)}` },
          ],
          columns: definition.columns.map((column) => ({
            label: column.label,
          })),
          rows: page.rows.map((row) => ({
            label: row.label,
            values: definition.columns.map((column) =>
              cell(column, row.values[column.id])
            ),
          })),
          others:
            page.others === null
              ? null
              : {
                  label: `Outros (${String(page.others.groups)})`,
                  values: definition.columns.map((column) =>
                    column.additive
                      ? cell(column, page.others?.values[column.id])
                      : '—'
                  ),
                },
          total: {
            label: 'Total',
            values: definition.columns.map((column) =>
              cell(column, page.totals.values[column.id])
            ),
          },
          notes: [
            ...(page.walkIn === null || page.walkIn.sales === 0
              ? []
              : [
                  `${money(page.walkIn.revenue)} em ${String(page.walkIn.sales)} venda(s) de balcão, sem cadastro — fora desta lista.`,
                ]),
            ...(report === 'vendedores'
              ? [
                  'Comissão: prévia do período, pela regra da tela de Comissões. O oficial é o do fechamento do mês.',
                ]
              : []),
          ],
        })

  /*
   * Os cartões do topo, montados do `totals`.
   *
   * Uma tabela de decisão e não JSX por relatório: a tela serve os quatro, e um
   * `if (report === …)` aqui seria o começo de quatro telas.
   */
  const totalOf = (id: string): string | null => page?.totals.values[id] ?? null
  const marginOf = totalOf('margin')
  const tiles: { label: string; value: string; hint?: string }[] =
    page === undefined
      ? []
      : definition.columns
          .filter((column) => TILE_COLUMNS.includes(column.id))
          .map((column) => ({
            label: TILE_LABEL[column.id] ?? column.label,
            value: cell(column, page.totals.values[column.id]),
            ...(column.id === 'profit' && marginOf !== null
              ? { hint: `margem de ${marginOf.replace('.', ',')}%` }
              : {}),
          }))

  const quality = page?.costQuality ?? null
  const costNote =
    quality === null || (quality.estimated === 0 && quality.missing === 0)
      ? null
      : [
          quality.estimated === 0
            ? null
            : `${String(quality.estimated)} ${quality.estimated === 1 ? 'item entrou' : 'itens entraram'} com custo estimado`,
          quality.missing === 0
            ? null
            : `${String(quality.missing)} sem custo cadastrado`,
        ]
          .filter((part): part is string => part !== null)
          .join(' · ')

  const coverage = page?.coverage ?? null
  const covered =
    coverage === null || coverage.total === 0
      ? null
      : Math.round((coverage.known * 100) / coverage.total)

  const reportSelect = (
    <Select
      aria-label="Relatório"
      value={choiceOfReport}
      className="w-56"
      onChange={(event) => {
        set({ relatorio: event.target.value as ReportChoice })
      }}
    >
      {REPORT_IDS.filter(
        // F366: a 4Play fica sem as variações (`balcao-anterior`).
        (entry) => entry !== 'variacoes' || !classic
      ).map((entry) => (
        <option key={entry} value={entry}>
          {REPORT_DEFINITIONS[entry].label}
        </option>
      ))}
      <option value={SALES_REPORT}>Vendas do período</option>
    </Select>
  )

  const periodPicker = (
    <PeriodPicker
      // F164: os atalhos à vista, como no painel — Hoje · Ontem · 7 dias ·
      // 30 dias · Este mês · Escolher data. O estado continua indo à URL.
      quick
      choice={choice}
      onChoiceChange={(next) => {
        set({ periodo: next })
      }}
      customFrom={customFrom}
      customTo={customTo}
      onCustomFromChange={(day) => {
        set({ de: day })
      }}
      onCustomToChange={(day) => {
        set({ ate: day })
      }}
      branches={branchList}
      branchId={branchId}
      onBranchChange={(next) => {
        set({ filial: next })
      }}
    />
  )

  if (salesList) {
    return (
      <Page>
        <PageHeader
          title="Relatórios"
          description="Cada venda do período com as peças e o valor total — de um cliente ou de todos. Para a nota no GDOOR, imprima ou salve em PDF."
        />
        <SalesPeriodReport
          range={
            range === null
              ? null
              : { from: range.from, to: range.to, end: exclusiveEnd(range.to) }
          }
          branchId={branchId}
          branchName={
            branchList.find((branch) => branch.id === branchId)?.name ?? 'Todas'
          }
          customerId={customerId}
          mode={salesMode}
          toolbar={(actions) => (
            <ListToolbar actions={actions}>
              {reportSelect}
              <Select
                aria-label="Como mostrar"
                value={salesMode}
                className="w-48"
                onChange={(event) => {
                  set({ modo: event.target.value as SalesReportMode })
                }}
              >
                <option value="detalhado">Venda por venda</option>
                <option value="consolidado">Só o total por dia</option>
              </Select>
              {periodPicker}
              <PartyPicker
                value={customerId}
                role="customer"
                placeholder="Todos os clientes"
                aria-label="Cliente"
                className="w-64"
                onChange={(next) => {
                  set({ cliente: next })
                }}
              />
              {customerId === '' ? null : (
                <Button
                  variant="ghost"
                  onClick={() => {
                    set({ cliente: '' })
                  }}
                >
                  Todos os clientes
                </Button>
              )}
            </ListToolbar>
          )}
        />
      </Page>
    )
  }

  return (
    <Page>
      <PageHeader
        title="Relatórios"
        description="Quem mais compra, o que mais sai, o que mais volta e quem mais vende — no período que você escolher."
      />

      <ListToolbar
        actions={
          /*
           * O arquivo leva o ranking, "Outros" e o "Total" — as mesmas linhas
           * da tela. Uma página só: o maior corte é de 2.500 linhas (F299), e
           * fingir paginação aqui seria cerimônia sem o problema.
           */
          <>
            <Button
              variant="secondary"
              disabled={printable === null}
              leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
              onClick={() => {
                setPrinting(true)
              }}
            >
              Imprimir
            </Button>
            <ExportMenu
              fileName={report}
              sheetName={definition.label}
              columns={reportExportColumns(report)}
              fetchPage={() =>
                Promise.resolve({
                  items: exportRows,
                  total: exportRows.length,
                })
              }
              pageSize={Math.max(exportRows.length, 1)}
              total={exportRows.length}
            />
          </>
        }
      >
        {reportSelect}

        {report === 'trocas' ? (
          <Select
            aria-label="Agrupar por"
            value={dimension}
            className="w-40"
            onChange={(event) => {
              set({ por: event.target.value as ExchangeDimension })
            }}
          >
            {EXCHANGE_DIMENSIONS.map((entry) => (
              <option key={entry} value={entry}>
                {DIMENSION_LABEL[entry]}
              </option>
            ))}
          </Select>
        ) : null}

        {periodPicker}

        {/* "Top 1" e "Top 10" são um clique cada — foi o pedido, literalmente. */}
        <Select
          aria-label="Corte"
          value={String(cut)}
          className="w-28"
          onChange={(event) => {
            set({ corte: Number(event.target.value) })
          }}
        >
          {REPORT_CUTS.map((entry) => (
            <option key={entry} value={entry}>
              {CUT_LABEL(entry)}
            </option>
          ))}
        </Select>
      </ListToolbar>

      {/*
        Os números do período, com a cara do painel (F164).

        Vêm do `totals` que a resposta já traz — o mesmo número da última linha
        da tabela —, então não há uma segunda conta que possa discordar. Só
        aparecem no relatório que tem aquela coluna: Trocas não tem faturamento,
        e um cartão de "R$ 0,00" ali seria pior que cartão nenhum.
      */}
      {tiles.length === 0 ? null : (
        <div
          role="group"
          aria-label="Números do período"
          className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
        >
          {tiles.map((tile) => (
            <StatTile
              key={tile.label}
              label={tile.label}
              value={tile.value}
              {...(tile.hint === undefined ? {} : { hint: tile.hint })}
              loading={query.isPending}
              error={query.isError}
            />
          ))}
        </div>
      )}

      {/*
        O quanto do custo é chute (F164): a mesma frase do painel. Um lucro
        calculado sem metade dos custos não pode passar por lucro de verdade.
      */}
      {costNote === null ? null : (
        <p className="text-(length:--text-xs) text-(--text-secondary)">
          {costNote}
        </p>
      )}

      {choice === 'custom' && range === null ? (
        <InlineAlert tone="warning">
          Escolha as duas datas, a primeira antes da segunda.
        </InlineAlert>
      ) : null}

      {/*
        O balcão sem cadastro, fora do ranking e dito em voz alta.
        Sem esta linha, alguém soma a coluna, compara com o painel e conclui que
        um dos dois está quebrado.
      */}
      {page?.walkIn == null || page.walkIn.sales === 0 ? null : (
        <InlineAlert tone="info">
          {`${money(page.walkIn.revenue)} em ${String(page.walkIn.sales)} venda${page.walkIn.sales === 1 ? '' : 's'} de balcão, sem cadastro — fora desta lista.`}
        </InlineAlert>
      )}

      {/*
        O que a comissão deste relatório É, e o que ela não é (F160).

        É a mesma conta da tela de Comissões — % do dia de cada venda, menos as
        trocas —, só que no período escolhido. O número OFICIAL é o do
        fechamento do mês, que também carrega o que ficou de meses anteriores.
        Sem a frase, alguém compara um recorte de quinze dias com o extrato do
        mês e conclui que um dos dois está errado.
      */}
      {report !== 'vendedores' || page === undefined ? null : (
        <p className="text-(length:--text-xs) text-(--text-secondary)">
          A comissão aqui é a prévia do período escolhido, pela mesma regra da
          tela de Comissões. O valor oficial é o do fechamento do mês.
        </p>
      )}

      {/*
        A cobertura. É a parte que faz o relatório de marca e de motivo valerem:
        um ranking sobre 15% do dado, sem esta frase, parece o retrato da loja.
      */}
      {coverage === null || covered === null || covered >= 100 ? null : (
        <InlineAlert tone="warning">
          {`${String(coverage.known)} de ${String(coverage.total)} ${coverage.unit === 'trocas' ? 'trocas têm motivo' : 'itens devolvidos têm essa informação'} (${String(covered)}%). O resto aparece na linha de baixo, e não foi escondido.`}
          {dimension === 'motivo' && report === 'trocas'
            ? ' Cada peça conta pelo motivo dela; quando ninguém deu um à peça, vale o motivo da troca (F178).'
            : ''}
        </InlineAlert>
      )}

      <QueryState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        onRetry={() => {
          void query.refetch()
        }}
        what="o relatório"
        isEmpty={(page?.rows.length ?? 0) === 0}
        empty={
          <EmptyState
            icon={<BarChart3 aria-hidden="true" className="h-8 w-8" />}
            title="Nada neste período"
            description="Escolha outro período, ou tire o filtro de filial."
          />
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell className="w-10">#</TableHeaderCell>
                <TableHeaderCell>{definition.label}</TableHeaderCell>
                {definition.columns.map((column) => (
                  <TableHeaderCell
                    key={column.id}
                    numeric={column.kind !== 'text' && column.kind !== 'date'}
                  >
                    {column.label}
                  </TableHeaderCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(page?.rows ?? []).map((row, index) => (
                <TableRow key={row.key ?? `vazio-${String(index)}`}>
                  <TableCell className="text-(--text-muted)">
                    {index + 1}
                  </TableCell>
                  <TableCell>
                    {/*
                      F299: o cliente abre o relatório dele — o que comprou no
                      período e o que mais compra —, no mesmo período e filial.
                    */}
                    {report === 'top-clientes' && row.key !== null ? (
                      <button
                        type="button"
                        className="text-left font-(--weight-medium) text-(--brand-primary) underline-offset-2 hover:underline"
                        title="Ver o que este cliente comprou e o que mais compra"
                        onClick={() => {
                          set({
                            relatorio: SALES_REPORT,
                            cliente: row.key ?? '',
                            modo: 'detalhado',
                          })
                        }}
                      >
                        {row.label}
                      </button>
                    ) : (
                      row.label
                    )}
                  </TableCell>
                  {definition.columns.map((column) => (
                    <TableCell
                      key={column.id}
                      numeric={column.kind !== 'text' && column.kind !== 'date'}
                    >
                      {cell(column, row.values[column.id])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}

              {page?.others == null ? null : (
                <TableRow className="text-(--text-muted)">
                  <TableCell />
                  <TableCell>{`Outros (${String(page.others.groups)})`}</TableCell>
                  {definition.columns.map((column) => (
                    <TableCell
                      key={column.id}
                      numeric={column.kind !== 'text' && column.kind !== 'date'}
                    >
                      {cell(column, page.others?.values[column.id])}
                    </TableCell>
                  ))}
                </TableRow>
              )}

              {page === undefined ? null : (
                <TableRow className="font-(--weight-medium)">
                  <TableCell />
                  <TableCell>Total</TableCell>
                  {definition.columns.map((column) => (
                    <TableCell
                      key={column.id}
                      numeric={column.kind !== 'text' && column.kind !== 'date'}
                    >
                      {cell(column, page.totals.values[column.id])}
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </QueryState>
      {/*
        O resumo por motivo fica em cima; a peça por peça, aqui (F178).

        Só na aba de Trocas, e com o MESMO período e filial do de cima: as duas
        listas respondem perguntas diferentes sobre o mesmo recorte, e discordar
        no filtro seria discordar no número.
      */}
      {report === 'trocas' && range !== null ? (
        <ReturnedPiecesPanel
          from={range.from}
          to={exclusiveEnd(range.to)}
          branchId={branchId}
        />
      ) : null}

      {printing && printable !== null ? (
        <PrintDocumentDialog
          document={printable}
          description={definition.label}
          initialPaper="a4"
          onClose={() => {
            setPrinting(false)
          }}
        />
      ) : null}
    </Page>
  )
}
